/**
 * ClesBatimentsPanel — gestion des clés (item 11.1, mig 274).
 *
 * ─── LA QUESTION À LAQUELLE CET ÉCRAN RÉPOND ────────────────────────────────
 *
 * « Il manque une clé de la salle des fêtes : laquelle, et qui l'a ? »
 *
 * D'où l'ordre d'affichage : ce qui est SORTI en premier, l'inventaire ensuite.
 * Un inventaire complet en tête ferait scroller pour trouver l'information
 * qu'on cherche neuf fois sur dix.
 *
 * ─── TROIS NIVEAUX, PARCE QUE LE TERRAIN EN A TROIS ─────────────────────────
 *
 *   Bâtiment → Porte (la serrure) → Exemplaires (les objets)
 *
 * Le niveau du milieu n'est pas décoratif : trois clés de la même porte sont
 * trois objets distincts, et sans eux on ne peut pas dire qui détient quoi.
 *
 * Le fond de plan viendra se greffer sur la PORTE (décision d'Henrico du
 * 29/07 : la liste d'abord). La place est déjà réservée en base.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Badge, Alert, Button, Input, Field } from '@/components/ui';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException, type LieuDTO } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { PlanSimple, type ElementPlan, type TraitPlan } from '@/components/PlanSimple';
import type { PorteBatiment, CleSortie, CleBatiment } from '@roronoa-collectivites/shared-types';
import { KeyRound, Plus, Info, AlertTriangle, Trash2, DoorClosed, Map, FileText } from 'lucide-react';
import { FaireSignerButton } from '@/components/signature/FaireSignerButton';

const fr = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');

/** La date du jour en AAAA-MM-JJ, heure locale. Jamais `toISOString()`. */
function aujourdHui(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function joursRestants(iso: string | null): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const cible = new Date(y!, (m ?? 1) - 1, d ?? 1);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((cible.getTime() - today.getTime()) / 86_400_000);
}

function EtatBadge({ c }: { c: CleBatiment }) {
  if (c.etat === 'perdue') return <Badge variant="urgent" size="sm">Perdue</Badge>;
  if (c.etat === 'detruite') return <Badge variant="neutral" size="sm">Détruite</Badge>;
  if (c.pret) {
    const j = joursRestants(c.pret.retourPrevuLe);
    if (j !== null && j < 0) {
      return <Badge variant="urgent" size="sm">En retard de {Math.abs(j)} j</Badge>;
    }
    return <Badge variant="warning" size="sm">Sortie</Badge>;
  }
  return <Badge variant="success" size="sm">Au coffre</Badge>;
}

export function ClesBatimentsPanel() {
  const { user } = useAuth();
  // `service_technique.cle.manage` : super_admin, admin, manager du ST (mig 274),
  // et le serveur ajoute `requireServiceMiddleware`. On reproduit la MÊME règle,
  // sinon l'écran afficherait des boutons qui renverraient 403.
  const peutGerer = !!user && (
    user.role === 'super_admin' || user.role === 'admin'
    || (user.role === 'manager'
        && (user.service === 'service_technique'
            || user.servicesAdditionnels.includes('service_technique')))
  );
  // Prêter et rendre est aussi ouvert à la GRC : c'est son geste quotidien sur
  // les salles réservées (`grc.cle.pret`).
  const peutPreter = peutGerer || (!!user && user.service === 'grc');

  const { confirm, prompt, dialogElement } = useConfirmDialog();

  const [portes, setPortes] = useState<PorteBatiment[]>([]);
  const [sorties, setSorties] = useState<CleSortie[]>([]);
  const [lieux, setLieux] = useState<LieuDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorCible, setErrorCible] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [formPorte, setFormPorte] = useState<{ lieuId: string; libelle: string; emplacement: string } | null>(null);

  // ── Le plan du bâtiment (mig 274/276) ──
  // Un bâtiment à la fois : un plan qui mélangerait la mairie et l'école ne
  // voudrait rien dire.
  const [planLieu, setPlanLieu] = useState<string>('');
  const [planPortes, setPlanPortes] = useState<ElementPlan[]>([]);
  const [planTraits, setPlanTraits] = useState<TraitPlan[]>([]);

  const chargerPlan = useCallback(async (lieuId: string) => {
    if (!lieuId) { setPlanPortes([]); return; }
    try {
      const r = await api.admin.lieux.planCles(lieuId);
      setPlanTraits(r.traits);
      setPlanPortes(r.portes.map((p) => ({
        id: p.id,
        code: p.libelle,
        libelle: p.emplacement ?? p.libelle,
        x: p.planX, y: p.planY,
        badge: p.nbCles > 0 ? `${p.nbSorties}/${p.nbCles} sorties` : 'aucune clé',
        // Une clé dehors se voit sur le plan : c'est l'information qu'on cherche.
        alerte: p.nbSorties > 0,
      })));
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Plan illisible');
    }
  }, []);

  async function deplacerPorte(id: string, x: number, y: number) {
    setPlanPortes((ps) => ps.map((p) => (p.id === id ? { ...p, x, y } : p)));
    try {
      await api.admin.lieux.positionnerPorte(id, { planX: x, planY: y });
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Position non enregistrée');
      await chargerPlan(planLieu);
    }
  }

  function erreurSur(cible: string | null, message: string) {
    setErrorCible(cible); setError(message);
  }

  const charger = useCallback(async () => {
    setLoading(true); setError(null); setErrorCible(null);
    try {
      const [rp, rs] = await Promise.all([
        api.admin.lieux.cles(),
        api.admin.lieux.clesSorties(),
      ]);
      setPortes(rp.portes); setSorties(rs.sorties);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  useEffect(() => {
    void (async () => {
      try { setLieux((await api.admin.lieux.list()).lieux); } catch { /* liste seule */ }
    })();
  }, []);

  async function creerPorte() {
    if (!formPorte?.lieuId || !formPorte.libelle.trim()) {
      setError('Choisissez un bâtiment et nommez la porte'); return;
    }
    setSaving(true); setError(null);
    try {
      await api.admin.lieux.creerPorte({
        lieuId: formPorte.lieuId,
        libelle: formPorte.libelle.trim(),
        emplacement: formPorte.emplacement || null,
        actif: true,
      });
      setFormPorte(null);
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Création impossible');
    } finally { setSaving(false); }
  }

  async function supprimerPorte(p: PorteBatiment) {
    const ok = await confirm({
      title: 'Supprimer cette porte ?',
      description: `« ${p.libelle} » sera effacée. Ce n’est possible que si aucune clé n’y est déclarée — l’historique des prêts ne doit pas disparaître.`,
      danger: true, confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    try {
      await api.admin.lieux.supprimerPorte(p.id);
      await charger();
    } catch (e) {
      erreurSur(p.id, e instanceof ApiException ? e.message : 'Suppression impossible');
    }
  }

  async function ajouterExemplaire(p: PorteBatiment) {
    const numero = await prompt({
      title: 'Nouvel exemplaire',
      description: `Le numéro d’exemplaire est attribué automatiquement et ne se réutilise jamais. Vous pouvez noter le numéro gravé sur la clé, s’il y en a un.`,
      placeholder: 'Numéro gravé (facultatif) — ex. 4412',
      confirmLabel: 'Ajouter',
    });
    if (numero === null) return;
    try {
      await api.admin.lieux.ajouterCle(p.id, { numeroGrave: numero || null });
      await charger();
    } catch (e) {
      erreurSur(p.id, e instanceof ApiException ? e.message : 'Ajout impossible');
    }
  }

  async function preter(p: PorteBatiment, c: CleBatiment) {
    const emprunteur = await prompt({
      title: `Prêter l’exemplaire ${c.exemplaire}`,
      description: `${p.lieuNom} · ${p.libelle}. À qui remettez-vous cette clé ? Le retour prévu se règle juste après.`,
      placeholder: 'Ex. : M. Martin, plombier',
      required: true, maxLength: 160,
      confirmLabel: 'Continuer',
    });
    if (!emprunteur) return;

    const retour = await prompt({
      title: 'Retour prévu le',
      description: 'Facultatif. Si vous le renseignez, un rappel partira 3 jours avant puis la veille.',
      placeholder: 'AAAA-MM-JJ',
      confirmLabel: 'Enregistrer le prêt',
      cancelLabel: 'Sans date de retour',
    });

    try {
      await api.admin.lieux.preterCle(c.id, {
        emprunteur,
        remiseLe: aujourdHui(),
        retourPrevuLe: retour && /^\d{4}-\d{2}-\d{2}$/.test(retour) ? retour : null,
      });
      setMsg(`Clé remise à ${emprunteur}.`);
      await charger();
    } catch (e) {
      erreurSur(p.id, e instanceof ApiException ? e.message : 'Prêt impossible');
    }
  }

  async function rendre(p: PorteBatiment, c: CleBatiment) {
    try {
      await api.admin.lieux.rendreCle(c.id, aujourdHui());
      setMsg(`Exemplaire ${c.exemplaire} rentré.`);
      await charger();
    } catch (e) {
      erreurSur(p.id, e instanceof ApiException ? e.message : 'Retour impossible');
    }
  }

  async function declarerPerdue(p: PorteBatiment, c: CleBatiment) {
    const ok = await confirm({
      title: `Déclarer l’exemplaire ${c.exemplaire} perdu ?`,
      description: 'La clé reste dans la liste, marquée perdue : c’est ce qui permet de savoir combien d’exemplaires circulent encore. Le numéro ne sera jamais réattribué.',
      danger: true, confirmLabel: 'Déclarer perdue',
    });
    if (!ok) return;
    try {
      await api.admin.lieux.changerEtatCle(c.id, 'perdue');
      await charger();
    } catch (e) {
      erreurSur(p.id, e instanceof ApiException ? e.message : 'Changement impossible');
    }
  }

  /**
   * Effacer une clé — pour une saisie fautive, pas pour une clé égarée.
   *
   * La différence n'est pas cosmétique : une clé perdue reste au registre parce
   * qu'elle circule encore et que son rang ne doit jamais désigner un autre
   * objet. Une clé effacée n'a jamais existé, et son rang redevient disponible.
   */
  async function supprimer(p: PorteBatiment, c: CleBatiment) {
    const nom = c.numeroGrave ? `n° ${c.numeroGrave}` : `exemplaire ${c.exemplaire}`;
    const ok = await confirm({
      title: `Supprimer la clé ${nom} ?`,
      description: 'À réserver aux erreurs de saisie. Une clé égarée se déclare '
        + 'perdue, elle ne se supprime pas : c’est ce qui permet de savoir combien '
        + 'd’exemplaires circulent encore. Le numéro d’exemplaire libéré sera repris '
        + 'par la prochaine clé de cette porte.',
      danger: true, confirmLabel: 'Supprimer définitivement',
    });
    if (!ok) return;
    try {
      await api.admin.lieux.supprimerCle(c.id);
      await charger();
    } catch (e) {
      erreurSur(p.id, e instanceof ApiException ? e.message : 'Suppression impossible');
    }
  }

  const champ = 'h-10 w-full rounded-md border border-border bg-surface/60 px-2 text-sm text-ink';

  return (
    <div className="space-y-4">
      {error && !errorCible && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      {/* ══ Ce qui est SORTI — la question du lundi matin ══ */}
      <Card variant="glass" padded>
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink">
          <KeyRound className="h-4 w-4 text-tech" aria-hidden="true" />
          Clés sorties
        </h2>
        {loading ? (
          <p className="text-sm text-ink-subtle">Chargement…</p>
        ) : sorties.length === 0 ? (
          <p className="text-sm text-ink-subtle">
            Toutes les clés sont rentrées.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {sorties.map((s) => {
              const j = joursRestants(s.retourPrevuLe);
              return (
                <li key={s.pretId} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                  <span className="text-ink">
                    {s.lieu} · {s.porte} · exemplaire {s.exemplaire}
                    {s.numeroGrave && <span className="text-ink-muted"> (n° {s.numeroGrave})</span>}
                  </span>
                  <span className="text-ink-muted">— {s.emprunteur}</span>
                  {/* M1 (retour Manon) — la remise de clé se signe (signature simple). */}
                  <FaireSignerButton documentType="remise_cles" documentId={s.pretId} signataireParDefaut={s.emprunteur} />
                  <span className="ml-auto">
                    {j !== null && j < 0
                      ? <Badge variant="urgent" size="sm">Retard de {Math.abs(j)} j</Badge>
                      : s.retourPrevuLe
                        ? <Badge variant="warning" size="sm">Retour le {fr(s.retourPrevuLe)}</Badge>
                        : <Badge variant="neutral" size="sm">Sans date de retour</Badge>}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* ══ Le plan du bâtiment ══ */}
      <Card variant="glass" padded>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <Map className="h-4 w-4 text-tech" aria-hidden="true" />
            Plan des portes
          </h3>
          <select
            aria-label="Bâtiment à afficher"
            className={champ + ' w-auto'}
            value={planLieu}
            onChange={(e) => { setPlanLieu(e.target.value); void chargerPlan(e.target.value); }}
          >
            <option value="">— Choisir un bâtiment —</option>
            {lieux.map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}
          </select>
        </div>
        {planLieu ? (
          <PlanSimple
            elements={planPortes}
            traits={planTraits}
            modifiable={peutGerer}
            onDeplacer={(id, x, y) => void deplacerPorte(id, x, y)}
            onRetirer={(id) => void deplacerPorte(id, 50, 50)}
            onTracer={(t) => void (async () => {
              try {
                await api.admin.lieux.ajouterTraitCles(planLieu, t);
                await chargerPlan(planLieu);
              } catch (e) {
                setError(e instanceof ApiException ? e.message : 'Trait non enregistré');
              }
            })()}
            onSupprimerTrait={(id) => void (async () => {
              try {
                await api.admin.lieux.supprimerTraitCles(id);
                await chargerPlan(planLieu);
              } catch (e) {
                setError(e instanceof ApiException ? e.message : 'Suppression impossible');
              }
            })()}
            vide="Aucune porte posée. Cliquez un nom sous le plan pour la placer, puis faites-la glisser."
          />
        ) : (
          <p className="text-sm text-ink-subtle">
            Choisissez un bâtiment pour voir ou dessiner l’implantation de ses portes.
          </p>
        )}
      </Card>

      {/* ══ L'inventaire ══ */}
      <Card variant="glass" padded>
        <div className="mb-3 flex items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <DoorClosed className="h-4 w-4 text-tech" aria-hidden="true" />
            Portes &amp; clés
          </h3>
          <div className="flex items-center gap-2">
            {/* L'impression complète, à portée d'agent. L'export Excel des clés
                existe, mais il vit dans l'écran Données réservé aux admins :
                celui qui cherche ce qu'ouvre le n° 15 n'a pas à passer par sa
                hiérarchie pour l'obtenir. */}
            <Button size="sm" variant="ghost"
              onClick={() => void api.admin.lieux.clesCommunePdf()
                .catch(() => setMsg('Téléchargement impossible.'))}>
              <FileText className="h-4 w-4" aria-hidden="true" /> Toutes les clés (PDF)
            </Button>
            {peutGerer && (
              <Button size="sm" onClick={() => setFormPorte({ lieuId: '', libelle: '', emplacement: '' })}>
                <Plus className="h-4 w-4" aria-hidden="true" /> Nouvelle porte
              </Button>
            )}
          </div>
        </div>

        <p className="mb-4 flex items-start gap-1.5 text-xs text-ink-muted">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Trois exemplaires d’une même clé sont trois objets distincts : c’est ce
            qui permet de dire <strong>laquelle</strong> est sortie, et chez qui.
            Un numéro d’exemplaire n’est <strong>jamais réattribué</strong>, même
            après une perte.
          </span>
        </p>

        {formPorte && (
          <div className="mb-4 grid gap-3 rounded-md border border-border bg-surface/40 p-3 sm:grid-cols-3">
            <Field label="Bâtiment" htmlFor="cle-lieu" required>
              <select id="cle-lieu" className={champ} value={formPorte.lieuId}
                onChange={(e) => setFormPorte({ ...formPorte, lieuId: e.target.value })}>
                <option value="">— Choisir —</option>
                {lieux.map((l) => <option key={l.id} value={l.id}>{l.nom}</option>)}
              </select>
            </Field>
            <Field label="Porte" htmlFor="cle-libelle" required>
              <Input id="cle-libelle" value={formPorte.libelle}
                placeholder="Porte principale"
                onChange={(e) => setFormPorte({ ...formPorte, libelle: e.target.value })} />
            </Field>
            <Field label="Emplacement" htmlFor="cle-empl" hint="Étage, aile, côté cour…">
              <Input id="cle-empl" value={formPorte.emplacement}
                onChange={(e) => setFormPorte({ ...formPorte, emplacement: e.target.value })} />
            </Field>
            <div className="flex gap-2 sm:col-span-3">
              <Button onClick={() => void creerPorte()} disabled={saving}>
                {saving ? 'Enregistrement…' : 'Créer la porte'}
              </Button>
              <Button variant="ghost" onClick={() => setFormPorte(null)}>Annuler</Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="py-6 text-center text-sm text-ink-subtle">Chargement…</p>
        ) : portes.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-subtle">
            Aucune porte déclarée. Commencez par les bâtiments dont les clés circulent
            le plus — salle des fêtes, ateliers, écoles.
          </p>
        ) : (
          <ul className="space-y-3">
            {portes.map((p) => (
              <li key={p.id} className="rounded-md border border-border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-ink">{p.lieuNom}</span>
                  <span className="text-ink-muted">·</span>
                  <span className="text-ink">{p.libelle}</span>
                  {p.emplacement && (
                    <span className="text-xs text-ink-subtle">({p.emplacement})</span>
                  )}
                  <span className="ml-auto flex items-center gap-2">
                    {peutGerer && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => void ajouterExemplaire(p)}>
                          <Plus className="h-4 w-4" aria-hidden="true" /> Exemplaire
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => void supprimerPorte(p)}
                          aria-label={`Supprimer la porte ${p.libelle}`}>
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </>
                    )}
                  </span>
                </div>

                {error && errorCible === p.id && (
                  <div className="mt-2"><Alert variant="error">{error}</Alert></div>
                )}

                {p.cles.length === 0 ? (
                  <p className="mt-2 text-xs text-ink-subtle">
                    Aucun exemplaire déclaré sur cette porte.
                  </p>
                ) : (
                  <ul className="mt-2 divide-y divide-border">
                    {p.cles.map((c) => (
                      <li key={c.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
                        {/* Le numéro GRAVÉ d'abord (31/07) : c'est celui que les
                            agents lisent sur le trousseau et se transmettent. Le
                            rang d'exemplaire est notre repère de traçabilité, il
                            passe en second. */}
                        <span className="text-ink">
                          {c.numeroGrave
                            ? <>n° {c.numeroGrave} <span className="text-ink-muted">· exemplaire {c.exemplaire}</span></>
                            : <>Exemplaire {c.exemplaire} <span className="text-ink-muted">· sans numéro</span></>}
                        </span>
                        {c.pret && (
                          <span className="text-xs text-ink-muted">
                            — chez {c.pret.emprunteur} depuis le {fr(c.pret.remiseLe)}
                            {c.pret.retourPrevuLe && `, retour prévu le ${fr(c.pret.retourPrevuLe)}`}
                          </span>
                        )}
                        {!c.pret && c.detenteur && (
                          <span className="text-xs text-ink-subtle">— {c.detenteur}</span>
                        )}
                        <span className="ml-auto flex items-center gap-2">
                          <EtatBadge c={c} />
                          {peutPreter && c.etat === 'disponible' && (
                            <Button size="sm" variant="ghost" onClick={() => void preter(p, c)}>
                              Prêter
                            </Button>
                          )}
                          {peutPreter && c.etat === 'pretee' && (
                            <Button size="sm" variant="ghost" onClick={() => void rendre(p, c)}>
                              Rentrée
                            </Button>
                          )}
                          {peutGerer && c.etat === 'disponible' && (
                            <Button size="sm" variant="ghost" onClick={() => void declarerPerdue(p, c)}>
                              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          )}
                          {/* Effacer une saisie fautive. Distinct de « perdue » :
                              une clé perdue existe encore quelque part et garde
                              son rang ; une clé effacée n'a jamais existé, et son
                              rang repart au pot commun. */}
                          {peutGerer && c.etat !== 'pretee' && (
                            <Button size="sm" variant="ghost" onClick={() => void supprimer(p, c)}>
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {dialogElement}
    </div>
  );
}
